import React from 'react';
import { Dispatch } from 'redux';
import {
  reduxForm,
  Field,
  Fields,
  InjectedFormProps,
  change,
} from 'redux-form';
import { Trans, withI18n, WithI18n } from 'react-i18next';
import LoadingButton from 'src/components/LoadingButton/';
import Alert from 'src/components/Alert';
import CategoryField from 'src/components/CategoryField';
import styles from './CreateForm.scss';
import validate from './createFormValidator';
import PitchField from './PitchField';
import AmountField from './AmountField';
import AmountWithCurrencyField from './AmountWithCurrencyField';
import { SupportedCurrency } from 'src/helpers/currencyMap';
import { FormattedMessage } from 'react-intl';
import CreateCharityEndorsedPage from 'src/components/CharityEndorsed/CreateCharityEndorsedPage';
import { CategoryResource } from 'src/helpers/api/categories';
import { CharityState } from 'src/redux/modules/charity/reducer';
import { AppState } from 'src/redux/modules';
import { ClickAnalyticsOptions } from 'src/redux/modules/analytics';
import { CreatePagePayload } from 'src/helpers/api/pages';

interface Props {
  analyticsClick: (options: ClickAnalyticsOptions) => void;
  categories: CategoryResource[];
  errorReason: string | null;
  initialValues: Partial<CreatePagePayload>;
  onCategoryChanged: (options: { categoryName: string }) => void;
  onSubmit: (data: any) => void;
  charity: CharityState;
  dispatch?: Dispatch<AppState>;
  supportedCurrencies: SupportedCurrency[];
}

interface State {
  isEndorsementAccepted: boolean;
}

export interface CreateFormData {
  categoryId: number;
  targetAmount: number;
  targetCurrency: Currency;
  pitchFor: string;
}

class CreateForm extends React.Component<
  Props & InjectedFormProps<CreateFormData, Props & WithI18n> & WithI18n,
  State
> {
  constructor(
    props: Props &
      InjectedFormProps<CreateFormData, Props & WithI18n> &
      WithI18n
  ) {
    super(props);

    if (__CLIENT__) {
      require('@justgiving/justgiving-bundle-head-js');
    }

    this.state = {
      isEndorsementAccepted: true,
    };
  }

  getErrorMessage = (reason: string) => {
    if (reason === 'rate_limit_exceeded') {
      return (
        <Alert id="creation-alert" type="red" displayType="inline" persistent>
          <p className="jg-space-mbn">
            <FormattedMessage
              id="CreateForm.pageCreationLimitHit"
              defaultMessage="We're sorry, you have reached the limit of new pages per day. For advise on creating pages going forward, please contact our {supportLink}."
              values={{
                supportLink: (
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://help.justgiving.com/hc/en-us/categories/201602465-Crowdfunding"
                  >
                    <FormattedMessage
                      id="CreateForm.pageCreationLimitHit.supportLink"
                      defaultMessage="customer support"
                    />
                  </a>
                ),
              }}
            />
          </p>
        </Alert>
      );
    }

    if (reason !== null) {
      return (
        <Alert id="create-alert" type="red" displayType="inline" persistent>
          <p className="jg-space-mbn">
            <FormattedMessage
              id="CreateForm.submissionErrorText"
              defaultMessage="An error has occurred, please try again"
            />
          </p>
        </Alert>
      );
    }

    return null;
  };

  changeEndorsementAcceptance = (
    checked: boolean,
    commitmentType: 'Endorsement' | 'Commitment'
  ) => {
    const { dispatch, charity } = this.props;

    dispatch!(change('create', 'charityId', checked ? charity!.id : null));
    dispatch!(change('create', 'commitmentType', commitmentType));

    this.setState({
      isEndorsementAccepted: checked,
    });
  };

  render() {
    const {
      charity,
      submitting,
      handleSubmit,
      invalid,
      pristine,
      categories,
      onCategoryChanged,
      analyticsClick,
      errorReason,
      supportedCurrencies,
    } = this.props;

    return (
      <div
        className={`${
          styles.container
        } jg-color--nero jg-text--center jg-background--white`}
      >
        <header
          className={`${
            styles.header
          } jg-background--white jg-text--pull-left jg-space-phsm`}
        >
          <h1
            className={`${
              styles.title
            } jg-space-pbxs jg-space-ptmd jg-space-ptlg@md`}
          >
            <FormattedMessage
              id="CreateForm.startCrowdfunding"
              defaultMessage="Let's get started"
            />
          </h1>
          <p className={`${styles.subtitle} jg-space-mbms jg-space-mbml@md`}>
            <Trans i18nKey="CreateForm.summariseWhatYoureDoing">
              Tell us what you're raising money for. To withdraw your donations,
              you will need a UK personal bank account.
            </Trans>
          </p>
        </header>
        <section
          className={`jg-background--white-smoke jg-bdt jg-bd--solid jg-bd--very-light-grey jg-space-ptms jg-space-ptlg@md jg-space-pbml jg-space-pbxl@md jg-text--pull-left`}
        >
          <form
            onSubmit={handleSubmit}
            className={`${styles.form} jg-space-phsm`}
          >
            {supportedCurrencies.length > 1 ? (
              <Fields
                names={['targetAmount', 'targetCurrency']}
                component={AmountWithCurrencyField}
                styles={styles}
                supportedCurrencies={supportedCurrencies}
              />
            ) : (
              <Fields
                names={['targetAmount', 'targetCurrency']}
                component={AmountField}
                styles={styles}
              />
            )}
            <Field
              name="pitchFor"
              component={PitchField}
              styles={styles}
              analyticsClick={analyticsClick}
              charity={charity}
            />
            <Field
              name="categoryId"
              component={CategoryField}
              categories={categories}
              onSelectionChanged={onCategoryChanged}
            />

            {charity && (
              <CreateCharityEndorsedPage
                charity={charity}
                isEndorsementAccepted={this.state.isEndorsementAccepted}
                changeEndorsementAcceptance={this.changeEndorsementAcceptance}
              />
            )}

            <footer
              className={`${styles.footer} jg-space-mtmd jg-space-mtml@md`}
            >
              {errorReason !== null && (
                <div className="jg-space-mbmd">
                  {this.getErrorMessage(errorReason)}
                </div>
              )}

              <LoadingButton
                loading={submitting}
                value="Continue"
                disabled={invalid || pristine}
                className={styles.action}
              />
            </footer>
          </form>
        </section>
      </div>
    );
  }
}

const form = reduxForm<CreateFormData, Props & WithI18n>({
  form: 'create',
  validate,
})(CreateForm) as React.ComponentClass<Props & WithI18n>;

export default withI18n()(form);



// WEBPACK FOOTER //
// ./src/components/CreateForm/CreateForm.tsx